// Scripted content for the AWS PPA intake demo wizard (see RenewalIntakeDemo).
// The story: the renewal proposal came in with everything attached — the wizard
// opens on the preliminary report (content mirrors preliminary-report-sample.html
// at the repo root), the documents are already read, and the discussion bot
// collects the five inputs the report's findings flagged as missing. All figures
// come from the shared PPA deal (../../demo/demoPpa.ts) so the Slack message
// and this wizard can never disagree.

import { DEMO_PPA, fmtM } from "../../demo/demoPpa";

const deal = DEMO_PPA;
const [y1, y2, y3] = deal.yearlyCommit;

export const RENEWAL_PROJECT_NAME = `${deal.vendor} PPA Renegotiation`;

/** A pre-attached document: its row copy, plus the pacing of its fake read. */
export interface ScriptedDocument {
    filename: string;
    size: string;
    type: string;
    /** How long the row shows the reading spinner before turning Ready. */
    parseMs: number;
}

export const RENEWAL_DOCUMENTS: ScriptedDocument[] = [
    {
        filename: "AWS_MSA_2023_executed.pdf",
        size: "1.1 MB",
        type: "Executed MSA",
        parseMs: 2200,
    },
    {
        filename: "PPA_Order_Form_2026_proposal.pdf",
        size: "348 KB",
        type: "PPA proposal",
        parseMs: 1600,
    },
];

// ---------------------------------------------------------------------------
// Step 1 · Preliminary report content (preliminary-report-sample.html; the
// sample's extracted-terms block is deliberately dropped)
// ---------------------------------------------------------------------------

/** One finding: a gap versus buyer-optimized norms, and the missing input
 * that decides whether it is material. */
export interface Finding {
    title: string;
    docs: string;
    market: string;
    materialIf: string;
    resolvedBy: string;
}

export const FINDINGS: Finding[] = [
    {
        title: "Commitment ramp",
        docs: `${deal.rampPctLabel} minimums, fixed, shortfall carried forward`,
        market: `${deal.marketRampLabel} typical for steady-state enterprise estates`,
        materialIf: "Usage forecast is flat to moderate",
        resolvedBy: "Usage forecast",
    },
    {
        title: "Savings Plan discounts",
        docs: `EC2 SP at ${deal.ec2SpDiscountPct}% · Compute SP at ${deal.computeSpDiscountPct}%`,
        market: `${deal.ec2SpMarketLabel} and ${deal.computeSpMarketLabel} achievable at the $150M+ tier`,
        materialIf: "Workloads are predictable and always-on",
        resolvedBy: "Workload mix + SP coverage",
    },
    {
        title: "Credit reliance",
        docs: `${fmtM(deal.creditsTotal)} in conditional, expiring credits (~4% of TCV)`,
        market: "Under 2–3% in buyer-optimized deals",
        materialIf: "Planned AI/ML and migration activity would not naturally hit the milestones",
        resolvedBy: "AI/ML + migration roadmap",
    },
    {
        title: "Contract posture",
        docs: `No egress caps · marketplace capped at ${deal.marketplaceCapPct}% · T4C suspended`,
        market: "Tiered egress, 25–50% marketplace, partial flexibility rights at this tier",
        materialIf: "Multi-region or data-heavy estate, or uncertain demand",
        resolvedBy: "Region footprint + flexibility needs",
    },
];

/** One locked full-analysis section and the inputs that unlock it. */
export interface LockedSection {
    name: string;
    sub: string;
    unlocks: string[];
    /** "clock" marks the partially-available section. */
    icon: "lock" | "clock";
    partial?: boolean;
}

export const LOCKED_SECTIONS: LockedSection[] = [
    {
        name: `Commercial levers (${deal.playbookLevers})`,
        sub: "High / target / low positions with dollar values and supporting arguments",
        unlocks: ["Usage forecast", "Commit history", "Workload mix + SP coverage"],
        icon: "lock",
    },
    {
        name: `Contractual must-haves (${deal.playbookMustHaves})`,
        sub: "Required amendments with suggested contract language",
        unlocks: ["Risk posture + contract priorities"],
        icon: "lock",
    },
    {
        name: "Execution sequence",
        sub: "Four ordered moves from opening position to redline",
        unlocks: ["Timeline + stakeholder map"],
        icon: "lock",
    },
    {
        name: "Leverage assessment and BATNA",
        sub: "Scored leverage dimensions and credible alternatives",
        unlocks: ["Multi-cloud posture"],
        icon: "lock",
    },
    {
        name: "Pricing benchmark and regional pricing",
        sub: "Full comparison tables across commitment, discounts, credits, downside terms",
        unlocks: ["Region footprint"],
        icon: "clock",
        partial: true,
    },
    {
        name: "Deal verdict",
        sub: "Proceed / proceed with conditions / renegotiate, with rationale",
        unlocks: ["Strategic context"],
        icon: "lock",
    },
];

// ---------------------------------------------------------------------------
// Steps 2–3 · Documents and conversation
// ---------------------------------------------------------------------------

/** How long the read-back "thinks" after the last document turns Ready. */
export const RENEWAL_ACK_MS = 1400;

/** The read-back under the documents — echoes the proposal's headline terms. */
export const RENEWAL_READ_BACK =
    `Both documents are read. The proposal commits ${fmtM(deal.tcv)} over ` +
    `${deal.termMonths} months (${deal.termStart} - ${deal.termEnd}), ramping ` +
    `${fmtM(y1)} → ${fmtM(y2)} → ${fmtM(y3)} with fixed annual minimums and ` +
    `shortfall carried forward. Headline terms: ${deal.crossServiceDiscountPct}% ` +
    `cross-service discount, EC2 Savings Plans at ${deal.ec2SpDiscountPct}%, ` +
    `Compute at ${deal.computeSpDiscountPct}%, ${fmtM(deal.creditsTotal)} in ` +
    `conditional credits, marketplace capped at ${deal.marketplaceCapPct}%, ` +
    `${deal.paymentTermsLabel}, and termination for convenience suspended for ` +
    "the term. Anything else worth attaching, like usage exports, competing " +
    "cloud quotes, or architecture plans? Otherwise, continue.";

/** The bot's opener when the conversation starts. */
export const RENEWAL_OPENER =
    "I have the deal picture from the executed MSA and the PPA proposal, so I " +
    "won't ask about anything the paper already answers. The findings flagged " +
    "five inputs it can't provide, starting here: the proposal ramps your " +
    "commitment 14% a year. What does demand actually look like: is " +
    "double-digit growth real, or bought ahead of plan?";

/** One scripted chat turn: the "user" answer and the bot's reply. Each send
 *  click plays the next one and checks off its topic in the Context rail. */
export interface ScriptedExchange {
    /** The Context-rail label this turn answers — named after the report's
     * "resolved by" inputs so the loop visibly closes. */
    topic: string;
    /** The hint line under the label, like the real Context checklist. */
    topicHint: string;
    user: string;
    reply: string;
    /** Typing-dots delay before the reply lands. */
    replyMs: number;
}

export const RENEWAL_EXCHANGES: ScriptedExchange[] = [
    {
        topic: "Usage forecast",
        topicHint: "Does demand support the 14% ramp?",
        user:
            "Real growth is more like 8 to 10%. The estate is steady-state and " +
            "mostly always-on: booking, loyalty, and PMS/CRS workloads with " +
            "seasonal peaks, and PCI scope control plus franchise-led IT budgets " +
            "keep expansion deliberate. The 14% ramp came from the AWS account " +
            "team's forecast, not ours.",
        reply:
            "Noted: an 8 to 10% demand reality against a 14% committed ramp, on " +
            "predictable, always-on workloads. That gap is commitment risk, not " +
            "growth. Next: how real is the multi-cloud story? Are Azure or GCP " +
            "credible for any tier of the estate, or is that just benchmarking " +
            "talk?",
        replyMs: 1600,
    },
    {
        topic: "Multi-cloud posture",
        topicHint: "Are Azure / GCP real alternatives?",
        user:
            "Credible for slices, not wholesale. We have an Azure footprint " +
            "through the Microsoft ecosystem and ran a GCP pilot on the analytics " +
            "stack last year that went well. Nobody believes we'd move the core, " +
            "but new AI and analytics workloads genuinely could land elsewhere.",
        reply:
            "That's the right shape of leverage: workload-level placement, not a " +
            "displacement threat. Credits next: the proposal carries " +
            `${fmtM(deal.creditsTotal)} in conditional credits. Will you actually ` +
            "hit the AI/ML spend milestones and the migration attestations?",
        replyMs: 1600,
    },
    {
        topic: "AI/ML + migration roadmap",
        topicHint: `Would the ${fmtM(deal.creditsTotal)} in credits actually pay?`,
        user:
            "Doubtful at full value. The AI/ML roadmap is real but the timing is " +
            "uncertain, and the migration count will likely fall short of the " +
            "attestation thresholds. Realistically we'd capture maybe half before " +
            "the credits expire.",
        reply:
            "So roughly half the headline credit value is breakage risk in " +
            "disguise. That matters. Two to go: egress. How is the estate " +
            "deployed across regions, and is data transfer already hurting?",
        replyMs: 1600,
    },
    {
        topic: "Region footprint + flexibility",
        topicHint: "Multi-region estate and egress exposure",
        user:
            "Multi-region across the US, EMEA, and APAC, with cross-region " +
            "replication growing as we consolidate the data platform. Egress is " +
            "already a line item finance flags every quarter, and nothing in the " +
            "proposal caps it.",
        reply:
            "Uncapped egress on a growing multi-region estate is exactly where " +
            "negotiated savings leak back out. Last one: what outcome do you " +
            "want, and is there anything the paper and the numbers don't show?",
        replyMs: 1600,
    },
    {
        topic: "Strategic context + timeline",
        topicHint: "Target outcome, timing, anything else",
        user:
            "We want to keep the 3-year term but fix the structure: a ramp we can " +
            "meet, deeper Savings Plan discounts, and credits converted into real " +
            "rate cuts. Around 10% off the commitment would clear our bar. One " +
            "more thing: our CFO heard the AWS account team is under pressure to " +
            "book this before their year-end.",
        reply:
            "Clear target: structural fixes worth roughly 10% of TCV on an " +
            "unchanged term, with year-end pressure as timing leverage. Combined " +
            "with the documents, the analyst has everything needed. Hit " +
            "“Request analysis” when you're ready.",
        replyMs: 1600,
    },
];
